import type { Library, SharePointSourceConfig } from "@/api/admin-api"

/**
 * Same set as backend SHAREPOINT_FAMILY_SOURCE_TYPES: every SharePoint-family type stores the
 * same app-registration auth shape (client_id, tenant_id, Key Vault secret reference).
 */
export const SHAREPOINT_COPY_SOURCE_TYPES: ReadonlySet<string> = new Set([
  "sharepoint",
  "spo",
  "spo-avtal",
  "spo-list",
  "spo-pages",
])

/** SharePoint-family types whose target is a generic list (items by Title), not a document library. */
export const SHAREPOINT_LIST_SOURCE_TYPES: ReadonlySet<string> = new Set(["spo-list", "spo-avtal"])

/** What the browse step should list for a given source type. */
export function browseItemKindFor(sourceType: string | undefined): "files" | "list_items" {
  return sourceType && SHAREPOINT_LIST_SOURCE_TYPES.has(sourceType) ? "list_items" : "files"
}

/** Placeholder the backend returns in place of a stored secret reference (never the value). */
export const MASKED_SECRET_INDICATOR = "***configured***"

export type LibraryAuthConfig = {
  client_id?: string
  tenant_id?: string
  client_secret?: string
}

export function getLibraryAuth(lib: Library): LibraryAuthConfig | undefined {
  return (lib.source_config as SharePointSourceConfig | undefined)?.auth
}

export function getLibraryHostname(lib: Library): string | undefined {
  return (lib.source_config as SharePointSourceConfig | undefined)?.hostname
}

/**
 * SharePoint fields that libraries migrated from config.json store in snake_case while the
 * dialogs and verify endpoints use camelCase. The indexer reads both (snake_case first), so the
 * admin UI reads and writes the same way.
 */
export type SpoCamelField = "siteName" | "listName" | "listId" | "siteId" | "folderPath"

const SPO_LEGACY_KEYS: Record<SpoCamelField, string> = {
  siteName: "site_name",
  listName: "list_name",
  listId: "list_id",
  siteId: "site_id",
  folderPath: "folder_path",
}

type SpoConfigLike = Record<string, unknown> | undefined

function asString(value: unknown): string {
  return typeof value === "string" ? value : ""
}

/**
 * Read a SharePoint field from either key shape. Key presence decides, like the indexer's
 * spo_config `_get`: an explicit legacy key wins even when empty; otherwise the camelCase key.
 */
export function readSpoField(config: SpoConfigLike, field: SpoCamelField): string {
  if (!config) return ""
  const legacy = SPO_LEGACY_KEYS[field]
  return legacy in config ? asString(config[legacy]) : asString(config[field])
}

/** Key to write a SharePoint field to: keep the legacy key when the config already uses it. */
export function spoFieldStorageKey(config: SpoConfigLike, field: SpoCamelField): string {
  const legacy = SPO_LEGACY_KEYS[field]
  return config && legacy in config ? legacy : field
}

/**
 * Full SharePoint Online hostname for the verify/browse endpoints, which only accept
 * `*.sharepoint.com`. Migrated libraries store just the tenant prefix (e.g. "xoneapp"); the
 * indexer's canonical_spo_tenant() tolerates both forms and so does this.
 */
export function expandSharePointHostname(hostname: string | undefined): string {
  const h = (hostname ?? "").trim().replace(/\.+$/, "")
  if (!h) return ""
  return h.includes(".") ? h : `${h}.sharepoint.com`
}

export function libraryHasCopyableAuth(lib: Library): boolean {
  if (!SHAREPOINT_COPY_SOURCE_TYPES.has(lib.source_type)) return false
  const auth = getLibraryAuth(lib)
  return Boolean(auth?.client_id?.trim() && auth.tenant_id?.trim())
}

/**
 * True when the library's stored auth carries a client secret reference (the admin API masks it
 * as `***configured***`; a raw document holds the `@kv:` reference). Saving with an empty secret
 * field keeps that reference, so verify can resolve it server-side through the library's own id
 * whether the field still shows the mask or the admin typed a name and cleared it again.
 */
export function hasStoredSecretReference(lib: Library | null | undefined): boolean {
  const secret = lib ? getLibraryAuth(lib)?.client_secret : undefined
  return typeof secret === "string" && secret.trim() !== ""
}

/**
 * Libraries whose auth can be copied, same-host entries first (otherwise in the given order).
 * `excludeLibraryId` drops the library being edited; the backend rejects self-copy anyway.
 */
export function getCopyAuthCandidates(
  libraries: Library[],
  options: { hostname?: string; excludeLibraryId?: string } = {}
): Library[] {
  const withAuth = libraries.filter(
    (lib) => lib.id !== options.excludeLibraryId && libraryHasCopyableAuth(lib)
  )
  const hostname = options.hostname
  if (!hostname) return withAuth
  const sameHost = withAuth.filter((lib) => getLibraryHostname(lib) === hostname)
  const rest = withAuth.filter((lib) => !sameHost.includes(lib))
  return [...sameHost, ...rest]
}
